import React, {useCallback, useState} from 'react'
import styled, {useTheme} from 'styled-components'
import Tooltip from '@material-ui/core/Tooltip'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import AssignmentIcon from '@material-ui/icons/Assignment'
import IconButton from 'lib/ui/IconButton'
import {useIfMounted} from 'lib/dom'
import {Rule} from 'Event/attendee-rules'

const STORAGE_KEY = 'obvio:rules-clipboard'

export function getCopiedRules(): Rule[] | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const CONFIRMATION_RESET_DELAY_MS = 1500

export default function RulesClipboardActions(props: {
  rules?: Rule[]
  onChange: (rules: Rule[]) => void
  className?: string
}) {
  const [copied, setCopied] = useState(false)
  const [pasted, setPasted] = useState(false)
  const ifMounted = useIfMounted()
  const theme = useTheme()

  const hasRules = Boolean(getCopiedRules()?.length)

  const resetAfterDelay = useCallback(
    (setConfirming: (confirming: boolean) => void) => {
      setConfirming(true)
      setTimeout(
        ifMounted(() => setConfirming(false)),
        CONFIRMATION_RESET_DELAY_MS,
      )
    },
    [ifMounted],
  )

  const handleCopy = () => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(props.rules ?? []))
    resetAfterDelay(setCopied)
  }

  const handlePaste = () => {
    const clipboardRules = getCopiedRules()
    if (!clipboardRules?.length) {
      return
    }

    props.onChange(clipboardRules)
    resetAfterDelay(setPasted)
  }

  return (
    <Container className={props.className}>
      <Label>Rules Clipboard</Label>
      <Tooltip title={copied ? 'Copied!' : 'Copy rules'}>
        <IconWrap>
          <IconButton aria-label="copy rules" onClick={handleCopy}>
            <FileCopyIcon style={{color: theme.colors.primary}} />
          </IconButton>
        </IconWrap>
      </Tooltip>
      <Tooltip
        title={
          pasted ? 'Pasted!' : hasRules ? 'Paste rules' : 'Copy rules first'
        }
      >
        <IconWrap>
          <IconButton
            aria-label="paste rules"
            disabled={!hasRules}
            onClick={handlePaste}
          >
            <AssignmentIcon
              style={{
                color: hasRules ? theme.colors.primary : theme.colors.disabled,
              }}
            />
          </IconButton>
        </IconWrap>
      </Tooltip>
    </Container>
  )
}

const Container = styled.div`
  position: sticky;
  top: -1px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[2]};
  padding: ${(props) => props.theme.spacing[2]} 0;
  margin-bottom: ${(props) => props.theme.spacing[2]};
  background-color: ${(props) => props.theme.colors.panel.background};
`

const Label = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(props) => props.theme.colors.text.muted};
  margin-right: auto;
`

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
`
